import { Form, Formik, useField } from "formik";
import * as Yup from "yup";
import { isYearRange, YEAR_RANGE_MESSAGE } from "../validation";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import type { Client } from "@/features/clients/types";
import type {
  Form2848Representative,
  FormSubmission,
  GenerateFor,
} from "@/features/forms/types";
import { FormEditorActions } from "./form-editor-actions";
import { FormikCell, FormikCheckbox } from "./formik-controls";
import { PoaRepresentativesTable } from "./poa-representatives-table";

// --- Form POA-1 editable payload -------------------------------------------
// snake_case keys mirror `apps/forms/mappings/form_poa1.py` `build_defaults`
// exactly (the submission `data` blob is passed through verbatim). POA-1 is the
// New York State Power of Attorney: the base POA skeleton (taxpayer + reps +
// matter/authority rows + toggles) PLUS a signer `title` field.

/** One NY tax-type authority row (checkbox + year/period; "other" adds explain). */
export interface FormPoa1TaxType {
  active: boolean;
  years: string;
  explain?: string;
}

/** The seven fixed NY tax-type authority categories. */
export type Poa1TaxTypeKey =
  | "all"
  | "corporation"
  | "partnership"
  | "personal_income"
  | "sales"
  | "withholding"
  | "other";

export interface Form_poa1_Data {
  generate_for: GenerateFor;
  representatives: Form2848Representative[];
  tax_types: Record<Poa1TaxTypeKey, FormPoa1TaxType>;
  authority_limits: string;
  rep_name_recv_copy: string;
  /** Signer's title — the POA-1 delta over the base POA skeleton. */
  title: string;
  // POA toggles.
  oic: boolean;
  conciliation: boolean;
  revoke_prior: boolean;
  delegate: boolean;
  sign_return: boolean;
}

const TAX_TYPE_ROWS: { key: Poa1TaxTypeKey; label: string }[] = [
  { key: "all", label: "All tax types" },
  { key: "corporation", label: "Corporation tax" },
  { key: "partnership", label: "Partnership" },
  { key: "personal_income", label: "Personal income tax" },
  { key: "sales", label: "Sales & use tax" },
  { key: "withholding", label: "Withholding tax" },
  { key: "other", label: "Other" },
];

const TOGGLES: { name: keyof Form_poa1_Data; label: string }[] = [
  { name: "oic", label: "Offer in Compromise" },
  { name: "conciliation", label: "Conciliation Conference / Tax Appeals Hearing" },
  { name: "revoke_prior", label: "Revoke all prior Powers of Attorney" },
  { name: "delegate", label: "Delegate authority to another representative" },
  { name: "sign_return", label: "Sign a return" },
];

const GENERATE_FOR_OPTIONS: { value: GenerateFor; label: string }[] = [
  { value: "taxpayer", label: "Taxpayer" },
  { value: "spouse", label: "Spouse" },
  { value: "jointly", label: "Jointly" },
];

const EMPTY_TAX_TYPE: FormPoa1TaxType = { active: false, years: "", explain: "" };

const schema = Yup.object({
  generate_for: Yup.string().oneOf(["taxpayer", "spouse", "jointly"]).required(),
  title: Yup.string().max(80, "Too long"),
  authority_limits: Yup.string().max(2000, "Too long"),
  rep_name_recv_copy: Yup.string().max(120, "Too long"),
  // Every granted (active) tax-type row's year/period must be a valid range —
  // the backend validates `years` on each active row (is_valid_year_range).
  tax_types: Yup.object().test(
    "active-years",
    YEAR_RANGE_MESSAGE,
    (val) =>
      !val ||
      Object.values(val as Record<string, { active?: boolean; years?: string }>).every(
        (row) => !row?.active || isYearRange(row.years),
      ),
  ),
});

function toInitialValues(data: Record<string, unknown>): Form_poa1_Data {
  const d = data as Partial<Form_poa1_Data>;
  const taxTypes = (d.tax_types ?? {}) as Partial<Record<Poa1TaxTypeKey, FormPoa1TaxType>>;
  const tax_types = Object.fromEntries(
    TAX_TYPE_ROWS.map(({ key }) => [
      key,
      {
        active: taxTypes[key]?.active ?? false,
        years: taxTypes[key]?.years ?? "",
        ...(key === "other" ? { explain: taxTypes[key]?.explain ?? "" } : {}),
      },
    ]),
  ) as Record<Poa1TaxTypeKey, FormPoa1TaxType>;

  return {
    generate_for: d.generate_for ?? "taxpayer",
    representatives: d.representatives ?? [],
    tax_types,
    authority_limits: d.authority_limits ?? "",
    rep_name_recv_copy: d.rep_name_recv_copy ?? "",
    title: d.title ?? "",
    oic: d.oic ?? false,
    conciliation: d.conciliation ?? false,
    revoke_prior: d.revoke_prior ?? false,
    delegate: d.delegate ?? false,
    sign_return: d.sign_return ?? false,
  };
}

function GenerateForField() {
  const [field, , helpers] = useField<GenerateFor>("generate_for");
  return (
    <RadioGroup
      className="flex flex-wrap gap-6"
      value={field.value}
      onValueChange={(v) => helpers.setValue(v as GenerateFor)}
    >
      {GENERATE_FOR_OPTIONS.map((o) => (
        <Label key={o.value} htmlFor={`gf-${o.value}`} className="flex items-center gap-2 font-normal">
          <RadioGroupItem id={`gf-${o.value}`} value={o.value} />
          {o.label}
        </Label>
      ))}
    </RadioGroup>
  );
}

function TitleField() {
  return (
    <div className="grid gap-2">
      <Label htmlFor="title">Title (signer)</Label>
      <FormikCell name="title" placeholder="e.g. President" aria-label="Signer title" />
    </div>
  );
}

function AuthorityLimitsField() {
  const [field] = useField("authority_limits");
  return (
    <div className="grid gap-2">
      <Label htmlFor="authority_limits">Limit the authority granted (explain)</Label>
      <Textarea id="authority_limits" rows={3} {...field} />
    </div>
  );
}

/** The seven NY tax-type authority rows: check the ones this POA covers and give
 * each a year/period; "Other" also takes a free-text description. */
function TaxTypesTable() {
  return (
    <Card className="gap-0 overflow-x-auto overflow-hidden p-0">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-16">Grant</TableHead>
            <TableHead>Tax Type</TableHead>
            <TableHead className="w-52">Year(s) or Period(s)</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {TAX_TYPE_ROWS.map(({ key, label }) => (
            <TableRow key={key}>
              <TableCell>
                <FormikCheckbox name={`tax_types.${key}.active`} aria-label={`Grant ${label}`} />
              </TableCell>
              <TableCell className="font-medium">
                {label}
                {key === "other" && (
                  <FormikCell
                    name="tax_types.other.explain"
                    className="mt-2"
                    placeholder="Describe other tax type"
                    aria-label="Other tax type description"
                  />
                )}
              </TableCell>
              <TableCell>
                <FormikCell
                  name={`tax_types.${key}.years`}
                  placeholder="e.g. 2021, 2022"
                  aria-label={`Years for ${label}`}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h3 className="text-foreground border-b pb-2 text-lg font-bold">{children}</h3>;
}

interface Props {
  client: Client;
  submission: FormSubmission;
  onSubmissionChange: (submission: FormSubmission) => void;
}

/**
 * Editable Form POA-1 (New York State Power of Attorney) — prefilled from the
 * client + its practitioners. On top of the base POA skeleton it grants
 * authority over the seven NY tax-type categories and captures the signer's
 * title. Save / Preview PDF / Save to Documents via the shared action bar.
 */
export function FormPoa1Editor({ client, submission, onSubmissionChange }: Props) {
  const address = [
    client.homeStreet,
    [client.homeCity, [client.homeState, client.homeZip].filter(Boolean).join(" ")]
      .filter(Boolean)
      .join(", "),
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <Formik
      initialValues={toInitialValues(submission.data)}
      validationSchema={schema}
      onSubmit={() => {}}
    >
      <Form className="flex flex-col gap-6">
        <Card className="gap-6 p-6">
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-foreground font-semibold">Generate for</span>
              <GenerateForField />
            </div>
            <div className="text-muted-foreground text-sm">
              <p className="text-foreground font-medium">{client.name}</p>
              {address && <p>{address}</p>}
            </div>
          </div>
        </Card>

        <section className="flex flex-col gap-4">
          <SectionHeading>Power of Attorney Given To</SectionHeading>
          <PoaRepresentativesTable />
        </section>

        <section className="flex flex-col gap-4">
          <SectionHeading>Tax Types & Periods</SectionHeading>
          <TaxTypesTable />
        </section>

        <Card className="gap-4 p-6">
          <SectionHeading>Authority</SectionHeading>
          <div className="grid gap-3 sm:grid-cols-2">
            {TOGGLES.map((t) => (
              <FormikCheckbox key={t.name} name={t.name} label={t.label} />
            ))}
          </div>
          <AuthorityLimitsField />
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="rep_name_recv_copy">Representative to receive a copy</Label>
              <FormikCell
                name="rep_name_recv_copy"
                placeholder="Rep name"
                aria-label="Representative to receive a copy"
              />
            </div>
            <TitleField />
          </div>
        </Card>

        <FormEditorActions
          submission={submission}
          clientId={client.id}
          onSubmissionChange={onSubmissionChange}
        />
      </Form>
    </Formik>
  );
}
